import { useEffect, useState } from "react";
import { getActiveProfile } from "../services/financeCore/clientTaxProfileService";
import { calculateWht, calculateGstWithheld, calculateExpectedReceipt, calculateRecoveryVariance, WHT_MODELS } from "../services/financeCore/whtCalculator";
import { checkRecoveryVariance } from "../services/financeCore/invoiceSnapshotService";

const format = (value) => Math.round(Number(value || 0)).toLocaleString("en-PK");

// Read-only panel every invoice page mounts before its approval button
// (requirement 5/8). Reflects whatever the client's active client_tax_profiles
// doc says — never a per-invoice checkbox — and surfaces the recovery
// variance so approval can be blocked/overridden in the caller.
export default function TaxTreatmentPanel({ clientCode, normalized, onProfileLoaded }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getActiveProfile(clientCode)
      .then((activeProfile) => {
        if (cancelled) return;
        setProfile(activeProfile);
        setError(activeProfile ? "" : `${clientCode} has no ACTIVE tax profile yet — WHT/GST-withholding will be recorded as 0 until finance activates one.`);
        onProfileLoaded?.(activeProfile);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [clientCode, onProfileLoaded]);

  if (loading) return <div className="panel-body page-copy">Loading tax treatment…</div>;

  const whtAmount = profile && normalized ? calculateWht(profile.whtModel, {
    serviceCharges: normalized.serviceChargeSubtotal,
    gstAmount: normalized.gstAmount,
    grossAmount: normalized.grossAmount,
    whtRate: profile.whtRate
  }) : 0;
  const gstWithheldAmount = profile && normalized ? calculateGstWithheld({
    gstAmount: normalized.gstAmount,
    gstWithholdingRate: profile.gstWithholdingRate
  }) : 0;
  const expectedReceipt = normalized ? calculateExpectedReceipt({
    grossAmount: normalized.grossAmount, whtAmount, gstWithheldAmount
  }) : 0;

  let variance = null;
  let varianceCheck = { withinTolerance: true, variance: null };
  if (profile?.recoveryMethod === "GROSS_UP_ADD_BACK" && normalized?.recoveryAddBack != null) {
    variance = calculateRecoveryVariance({ actualWhtDeducted: whtAmount, recoveryAddBack: normalized.recoveryAddBack });
    varianceCheck = checkRecoveryVariance(variance, profile.recoveryVarianceTolerance);
  }

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-header">
        <h3 className="panel-title">Tax Treatment</h3>
        <span className="badge">{profile ? profile.whtModel : "NO ACTIVE PROFILE"}</span>
      </div>
      <div className="panel-body">
        {error && <p className="page-copy" role="status">{error}</p>}
        {profile && (
          <table className="data-table">
            <tbody>
              <tr><td>WHT model</td><td>{profile.whtModel === WHT_MODELS.SERVICE_CHARGES_PLUS_GST ? "Service Charges + GST" : "Gross Invoice with Recovery"}</td></tr>
              <tr><td>WHT rate</td><td>{(profile.whtRate * 100).toFixed(2)}%</td></tr>
              <tr><td>GST rate</td><td>{(profile.gstRate * 100).toFixed(2)}%</td></tr>
              <tr><td>GST withholding rate</td><td>{(profile.gstWithholdingRate * 100).toFixed(2)}%</td></tr>
              {profile.recoveryMethod === "GROSS_UP_ADD_BACK" && (
                <tr><td>WHT recovery rate</td><td>{profile.recoveryRate != null ? `${(profile.recoveryRate * 100).toFixed(4)}%` : "Not configured"}</td></tr>
              )}
              <tr><td>Expected WHT</td><td>PKR {format(whtAmount)}</td></tr>
              <tr><td>Expected GST withholding</td><td>PKR {format(gstWithheldAmount)}</td></tr>
              <tr><td>Expected receipt</td><td><strong>PKR {format(expectedReceipt)}</strong></td></tr>
              {variance != null && (
                <tr>
                  <td>Recovery variance</td>
                  <td style={{ color: varianceCheck.withinTolerance ? "inherit" : "#b42318" }}>
                    PKR {format(variance)} {!varianceCheck.withinTolerance && "— exceeds tolerance, override required"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
